import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import ChatIcon from "@material-ui/icons/Chat";
import DeleteIcon from "@material-ui/icons/Delete";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import QRGeneratorView from "../../AdminTools/StockModule/QRGeneratorView";
import DialogAppBar from "../../Components/DialogAppBar";
import MediaFiles from "../../Components/MediaFiles";
import NoteSection from "../../Components/NoteSection";
import Title from "../../Components/Title";
import { showPopupSnackbar } from "../../redux/actions/snackbarActions";
import {
  ECABIN,
  USER_GROUPS,
  checkRequiredPermissions,
  formatTimeWithTZOffset,
  getToken,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import DetailTabs from "../DeviceDetails/DetailTabs";

const server = EnvSettings.server;

const parseFeatures = (features) => {
  if (!features) {
    return {};
  }
  if (typeof features === "string") {
    try {
      return JSON.parse(features);
    } catch (error) {
      return {};
    }
  }
  return features;
};

const normalizeFeatureList = (features = []) =>
  (Array.isArray(features) ? features : []).map((feature) => ({
    name:
      typeof feature?.name === "string"
        ? feature.name.trim()
        : feature?.name || "",
    description:
      typeof feature?.description === "string"
        ? feature.description.trim() || null
        : null,
  }));

const formatValue = (value, fallback) =>
  value === undefined || value === null || value === "" ? fallback : value;

const formatCost = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(2) : null;

export default function ManufacturingEntryDialog({
  entry,
  open,
  onClose,
  onRefresh,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const parsedFeatures = useMemo(() => parseFeatures(entry?.features), [entry]);
  const storedFeatures = useMemo(() => {
    if (Array.isArray(parsedFeatures)) {
      return parsedFeatures;
    }
    return parsedFeatures?.features ?? [];
  }, [parsedFeatures]);
  const normalizedSelectedFeatures = useMemo(
    () => normalizeFeatureList(storedFeatures),
    [storedFeatures],
  );
  const [featuresDraft, setFeaturesDraft] = useState(
    normalizedSelectedFeatures,
  );
  const [newFeatureName, setNewFeatureName] = useState("");
  const [newFeatureDescription, setNewFeatureDescription] = useState("");
  const [isSavingFeatures, setIsSavingFeatures] = useState(false);
  const [showQRGenerator, setShowQRGenerator] = useState(false);
  const canEditFeatures = checkRequiredPermissions([
    USER_GROUPS.ADMIN,
    USER_GROUPS.PRODUCTION,
  ]);

  useEffect(() => {
    setFeaturesDraft(normalizedSelectedFeatures);
  }, [entry?.id, normalizedSelectedFeatures]);

  const featuresChanged = useMemo(() => {
    if (featuresDraft.length !== normalizedSelectedFeatures.length) {
      return true;
    }
    return featuresDraft.some((feature, index) => {
      const original = normalizedSelectedFeatures[index];
      if (!original) {
        return true;
      }
      return (
        feature.name !== original.name ||
        (feature.description || null) !== (original.description || null)
      );
    });
  }, [featuresDraft, normalizedSelectedFeatures]);

  const stockChanges = useMemo(() => {
    if (!entry) {
      return [];
    }
    if (Array.isArray(entry.StockChanges)) {
      return entry.StockChanges;
    }
    if (entry.StockChange) {
      return [entry.StockChange];
    }
    return [];
  }, [entry]);

  const resultingStockChange = useMemo(() => {
    return stockChanges.find((stockChange) => {
      const events = Array.isArray(stockChange?.events)
        ? stockChange.events
        : [];
      return events.some((event) => Number(event?.manufacturing) > 0);
    });
  }, [stockChanges]);

  const componentStockChanges = useMemo(() => {
    const extracted = stockChanges
      .map((stockChange) => {
        const events = Array.isArray(stockChange?.events)
          ? stockChange.events
          : [];
        const manufacturingImpact = events.reduce((sum, event) => {
          const value = Number(event?.manufacturing);
          return Number.isNaN(value) ? sum : sum + value;
        }, 0);
        if (manufacturingImpact >= 0) {
          return null;
        }
        return {
          id: stockChange.id,
          StockItem: stockChange.StockItem,
          stockItemId: stockChange.StockItemId,
          quantity: Math.abs(manufacturingImpact),
          Warehouse: stockChange.Warehouse,
        };
      })
      .filter(Boolean);
    if (extracted.length) {
      return extracted;
    }
    return (parsedFeatures?.componentStockChanges || []).map(
      (component, index) => ({
        id: component.stockChangeId || index,
        StockItem: component.StockItem,
        stockItemId: component.stockItemId,
        quantity: component.quantity,
      }),
    );
  }, [parsedFeatures, stockChanges]);

  const recipeMetadata = useMemo(() => {
    if (!entry?.metadata) {
      return null;
    }
    if (entry.metadata.recipe) {
      return entry.metadata.recipe;
    }
    return entry.metadata;
  }, [entry]);

  const metadataServiceItems = useMemo(() => {
    if (!recipeMetadata?.services?.length) {
      return [];
    }
    return recipeMetadata.services
      .map((service, index) => {
        const details =
          service.serviceItem || service.ServiceItem || service || null;
        if (!details) {
          return null;
        }
        return {
          id: service.id || service.manufacturingRecipeServiceItemId || index,
          serviceItemId:
            service.serviceItemId ||
            service.ServiceItemId ||
            details.id ||
            null,
          name: details.name || "",
          sku: details.sku || "",
          costPrice:
            details.costPrice !== undefined && details.costPrice !== null
              ? Number(details.costPrice)
              : null,
          quantity:
            service.quantity !== undefined
              ? service.quantity
              : service.count !== undefined
                ? service.count
                : null,
        };
      })
      .filter(Boolean);
  }, [recipeMetadata]);

  const metadataComponentsMap = useMemo(() => {
    const map = new Map();
    (recipeMetadata?.components || []).forEach((component) => {
      const identifier =
        component.stockItemId ||
        component.StockItemId ||
        component.stockItem?.id ||
        component.StockItem?.id;
      if (identifier === undefined || identifier === null) {
        return;
      }
      map.set(String(identifier), component);
    });
    return map;
  }, [recipeMetadata]);

  const bomItems = useMemo(() => {
    const componentEntries = componentStockChanges.map((component, index) => ({
      key: `component-${component.id || index}`,
      type: "component",
      sku: component.StockItem?.sku || component.stockItemId || null,
      name: component.StockItem?.name || null,
      quantity:
        component.quantity !== undefined && component.quantity !== null
          ? component.quantity
          : null,
      cost: (() => {
        const match =
          metadataComponentsMap.get(
            String(
              component.stockItemId ||
                component.StockItemId ||
                component.StockItem?.id ||
                "",
            ),
          ) || null;
        const stockItemSnapshot = match?.stockItem || match?.StockItem || null;
        const metadataCost =
          stockItemSnapshot?.costPrice !== undefined &&
          stockItemSnapshot?.costPrice !== null
            ? Number(stockItemSnapshot.costPrice)
            : null;
        if (metadataCost !== null) {
          const quantityValue =
            component.quantity !== undefined && component.quantity !== null
              ? Number(component.quantity)
              : null;
          return quantityValue !== null
            ? metadataCost * quantityValue
            : metadataCost;
        }
        const directCost =
          component.StockItem?.costPrice !== undefined &&
          component.StockItem?.costPrice !== null
            ? Number(component.StockItem.costPrice)
            : null;
        if (directCost === null) {
          return null;
        }
        const quantityValue =
          component.quantity !== undefined && component.quantity !== null
            ? Number(component.quantity)
            : null;
        return quantityValue !== null ? directCost * quantityValue : directCost;
      })(),
    }));
    const serviceEntries = metadataServiceItems.map((service, index) => ({
      key: `service-${service.id || index}`,
      type: "service",
      sku: service.sku || null,
      name: service.name || null,
      quantity: service.quantity ?? null,
      cost: (() => {
        const unitCost =
          service.costPrice !== undefined && service.costPrice !== null
            ? Number(service.costPrice)
            : (service.cost ?? null);
        if (unitCost === null) {
          return null;
        }
        const quantityValue =
          service.quantity !== undefined && service.quantity !== null
            ? Number(service.quantity)
            : 1;
        return unitCost * (Number.isFinite(quantityValue) ? quantityValue : 1);
      })(),
    }));
    return [...componentEntries, ...serviceEntries];
  }, [componentStockChanges, metadataComponentsMap, metadataServiceItems]);

  const bomCostSummary = useMemo(() => {
    const entryTotal = entry?.totalCost;
    if (entryTotal !== undefined && entryTotal !== null) {
      const batchSizeValue = Number(entry?.batchSize);
      const normalizedBatchSize =
        Number.isFinite(batchSizeValue) && batchSizeValue > 0
          ? batchSizeValue
          : null;
      const totalValue = Number(entryTotal);
      return {
        total: totalValue,
        unitTotal: normalizedBatchSize
          ? totalValue / normalizedBatchSize
          : totalValue,
        hasCost: true,
      };
    }
    const costs = bomItems
      .map((item) => Number(item.cost))
      .filter((value) => Number.isFinite(value));
    const batchSizeValue = Number(entry?.batchSize);
    const normalizedBatchSize =
      Number.isFinite(batchSizeValue) && batchSizeValue > 0
        ? batchSizeValue
        : null;
    return {
      total: costs.reduce((sum, value) => sum + value, 0),
      unitTotal: normalizedBatchSize
        ? costs.reduce((sum, value) => sum + value, 0) / normalizedBatchSize
        : costs.reduce((sum, value) => sum + value, 0),
      hasCost: costs.length > 0,
    };
  }, [bomItems, entry?.batchSize, entry?.totalCost]);

  if (!entry) {
    return null;
  }

  const serialised = entry?.SerialisedSubAssembly;
  const formattedCreatedAt = entry?.createdAt
    ? formatTimeWithTZOffset(entry.createdAt, ECABIN)
    : t("common.notAvailable", "N/A");

  const handleNotesReload = () => {
    if (typeof onRefresh === "function") {
      onRefresh();
    }
  };

  const billOfMaterialsContent = (
    <>
      <Title>{t("manufacturing.billOfMaterials", "Bill of materials")}</Title>
      {bomItems.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("manufacturing.itemType", "Type")}</TableCell>
              <TableCell>{t("stock.sku", "SKU")}</TableCell>
              <TableCell>{t("stock.item", "Item")}</TableCell>
              <TableCell align="right">
                {t("stock.quantity", "Quantity")}
              </TableCell>
              <TableCell align="right">
                {t("manufacturing.cost", "Cost")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bomItems.map((item) => (
              <TableRow key={item.key}>
                <TableCell>
                  {item.type === "component"
                    ? t("manufacturing.typeComponent", "Component")
                    : t("manufacturing.typeService", "Service")}
                </TableCell>
                <TableCell>
                  {formatValue(item.sku, t("common.notAvailable", "N/A"))}
                </TableCell>
                <TableCell>
                  {formatValue(item.name, t("common.notAvailable", "N/A"))}
                </TableCell>
                <TableCell align="right">
                  {item.type === "component"
                    ? formatValue(
                        item.quantity,
                        t("common.notAvailable", "N/A"),
                      )
                    : item.quantity !== null && item.quantity !== undefined
                      ? item.quantity
                      : "—"}
                </TableCell>
                <TableCell align="right">
                  {formatValue(
                    formatCost(item.cost ?? item.costPrice),
                    t("common.notAvailable", "N/A"),
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={4} align="right"></TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2">
                  {t("common.total", "Total")}:{" "}
                  {bomCostSummary.hasCost
                    ? formatCost(bomCostSummary.total)
                    : t("common.notAvailable", "N/A")}
                </Typography>
                {bomCostSummary.hasCost ? (
                  <Typography variant="caption" color="textSecondary">
                    {t("manufacturing.unitTotal", "Unit total")}:{" "}
                    {formatCost(bomCostSummary.unitTotal)}
                  </Typography>
                ) : null}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {t("manufacturing.noBomItems", "No bill of materials recorded.")}
        </Typography>
      )}
    </>
  );

  const handleAddFeature = () => {
    const trimmedName = newFeatureName.trim();
    if (!trimmedName) {
      return;
    }
    setFeaturesDraft((prev) => [
      ...prev,
      {
        name: trimmedName,
        description: newFeatureDescription.trim()
          ? newFeatureDescription.trim()
          : null,
      },
    ]);
    setNewFeatureName("");
    setNewFeatureDescription("");
  };

  const handleRemoveFeature = (index) => {
    setFeaturesDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCancelFeatureChanges = () => {
    setFeaturesDraft(normalizedSelectedFeatures);
    setNewFeatureName("");
    setNewFeatureDescription("");
  };

  const handleSaveFeatures = () => {
    if (!entry?.id || isSavingFeatures || !featuresChanged) {
      return;
    }
    const payload = normalizeFeatureList(featuresDraft).filter(
      (feature) => feature.name,
    );
    setIsSavingFeatures(true);
    fetch(server + "/edit_manufacturing_entry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        manufacturingEntryId: entry.id,
        features: payload,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          const updatedFeatures = normalizeFeatureList(
            result.manufacturingEntry?.features,
          );
          setFeaturesDraft(updatedFeatures);
          dispatch(
            showPopupSnackbar({
              message: t(
                "manufacturing.featuresUpdated",
                "Manufacturing features updated.",
              ),
              severity: "success",
            }),
          );
          if (typeof onRefresh === "function") {
            onRefresh();
          }
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.featuresUpdateFailed",
              "Failed to update manufacturing features.",
            ),
            severity: "error",
          }),
        );
      })
      .finally(() => setIsSavingFeatures(false));
  };

  const manufacturingFeaturesContent = (
    <>
      <Title>
        {t("manufacturing.manufacturingFeatures", "Manufacturing features")}
      </Title>
      {featuresDraft.length ? (
        <List dense>
          {featuresDraft.map((feature, index) => (
            <ListItem key={`${feature.name || "feature"}-${index}`} divider>
              <ListItemText
                primary={feature.name || t("common.notAvailable", "N/A")}
                secondary={
                  feature.description ? (
                    <Typography variant="body2" color="textSecondary">
                      {feature.description}
                    </Typography>
                  ) : null
                }
              />
              {canEditFeatures ? (
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label={t("delete", "Delete")}
                    onClick={() => handleRemoveFeature(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              ) : null}
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {t(
            "manufacturing.noSelectedFeatures",
            "No additional features selected.",
          )}
        </Typography>
      )}
      {canEditFeatures ? (
        <>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  label={t("manufacturing.featureName", "Feature name")}
                  value={newFeatureName}
                  onChange={(e) => setNewFeatureName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  label={t(
                    "manufacturing.featureDescription",
                    "Feature description",
                  )}
                  value={newFeatureDescription}
                  onChange={(e) => setNewFeatureDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  disabled={!newFeatureName.trim()}
                  onClick={handleAddFeature}
                >
                  {t("manufacturing.addFeature", "Add feature")}
                </Button>
              </Grid>
            </Grid>
          </Box>
          <Box
            mt={2}
            display="flex"
            alignItems="center"
            style={{ gap: 8, flexWrap: "wrap" }}
          >
            <Button
              color="primary"
              variant="contained"
              onClick={handleSaveFeatures}
              disabled={isSavingFeatures || !featuresChanged}
            >
              {isSavingFeatures
                ? t("common.saving", "Saving…")
                : t("common.save", "Save")}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancelFeatureChanges}
              disabled={!featuresChanged || isSavingFeatures}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Box>
        </>
      ) : null}
    </>
  );

  const mediaContent = entry?.uuid ? (
    <>
      <MediaFiles
        resource={{
          type: "manufacturing",
          number: entry.uuid,
        }}
      />
    </>
  ) : (
    <>
      <Title>{t("media", "Media")}</Title>
      <Typography variant="body2" color="textSecondary">
        {t(
          "manufacturing.noManufacturingMedia",
          "Media files are unavailable for this entry.",
        )}
      </Typography>
    </>
  );

  const notesContent =
    entry?.id != null ? (
      <NoteSection
        parent={{ ManufacturingEntryId: entry.id }}
        notes={entry?.Notes}
        reload={handleNotesReload}
        noPaper={true}
      />
    ) : (
      <>
        <Title>{t("notes", "Notes")}</Title>
        <Typography variant="body2" color="textSecondary">
          {t(
            "manufacturing.notesUnavailable",
            "Notes are unavailable for this entry.",
          )}
        </Typography>
      </>
    );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogContent>
        <DialogAppBar
          header={`${t("manufacturing.entryDetails", "Manufacturing entry")}${
            entry?.uuid ? ` - ${entry.uuid}` : ""
          }`}
          onClose={onClose}
        />
        <Dialog
          open={showQRGenerator}
          onClose={() => setShowQRGenerator(false)}
        >
          <DialogContent style={{ minWidth: 350 }}>
            <QRGeneratorView
              value={entry?.StockItem?.sku || ""}
              labeldata={{
                sku: entry?.StockItem?.sku || null,
                batch: entry?.batchNumber || null,
              }}
            />
          </DialogContent>
        </Dialog>
        <Grid
          container
          spacing={3}
          style={{ marginTop: 60, marginBottom: 20, paddingBottom: 30 }}
        >
          <Grid item xs={12}>
            <Paper>
              <Box p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Title>
                      {t("manufacturing.entryDetails", "Manufacturing entry")}
                    </Title>
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("createdAt", "Created")}
                      </Typography>
                      <Typography variant="body1">
                        {formattedCreatedAt}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("manufacturing.batchNumber", "Batch number")}
                      </Typography>
                      <Typography variant="body1">
                        {formatValue(
                          entry?.batchNumber,
                          t("common.notAvailable", "N/A"),
                        )}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("manufacturing.batchSize", "Batch size")}
                      </Typography>
                      <Typography variant="body1">
                        {formatValue(
                          entry?.batchSize,
                          t("common.notAvailable", "N/A"),
                        )}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t(
                          "manufacturing.resultingStockItem",
                          "Resulting item",
                        )}
                      </Typography>
                      <Typography variant="body1">
                        {entry?.StockItem?.name ??
                          t("common.notAvailable", "N/A")}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {entry?.StockItem?.sku}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t(
                          "manufacturing.manufacturingCost",
                          "Manufacturing Cost",
                        )}
                      </Typography>
                      <Typography variant="body1">
                        {formatValue(
                          formatCost(entry?.totalCost),
                          t("common.notAvailable", "N/A"),
                        )}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("manufacturing.manufacturedIn", "Manufactured in")}
                      </Typography>
                      <Typography variant="body1">
                        {resultingStockChange?.Warehouse?.label ??
                          t("common.notAvailable", "N/A")}
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        onClick={() => setShowQRGenerator(true)}
                      >
                        {t("stockModule.showQRCodeLabel", "Show QR Code Label")}
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Title>
                      {t(
                        "manufacturing.serialisedSubAssemblyDetails",
                        "Serialised sub-assembly details",
                      )}
                    </Title>
                    {serialised ? (
                      <>
                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t(
                              "serialisedSubAssembly.number",
                              "Chassis number",
                            )}
                          </Typography>
                          <Typography variant="body1">
                            {serialised.number ??
                              t("common.notAvailable", "N/A")}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t("serialisedSubAssembly.model", "Model")}
                          </Typography>
                          <Typography variant="body1">
                            {serialised.model ??
                              t("common.notAvailable", "N/A")}
                          </Typography>
                        </Box>
                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            {t("serialisedSubAssembly.color", "Color")}
                          </Typography>
                          <Typography variant="body1">
                            {serialised.color ??
                              t("common.notAvailable", "N/A")}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {t(
                          "manufacturing.noSerialisedSubAssembly",
                          "No serialised sub-assembly created.",
                        )}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <DetailTabs
              activeTabs={[
                {
                  label: t(
                    "manufacturing.billOfMaterials",
                    "Bill of materials",
                  ),
                  content: billOfMaterialsContent,
                },
                {
                  label: t(
                    "manufacturing.manufacturingFeatures",
                    "Manufacturing features",
                  ),
                  content: manufacturingFeaturesContent,
                },
                {
                  label: t("media", "Media"),
                  content: mediaContent,
                },
                {
                  label: (
                    <>
                      <ChatIcon
                        style={{
                          fontSize: 20,
                          marginRight: 4,
                          verticalAlign: "middle",
                        }}
                      />
                      {t("notes", "Notes")}
                    </>
                  ),
                  content: notesContent,
                },
              ]}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
